import { useState } from "react";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { normalizeFriendName } from "@boo/shared";
import { Button } from "@/components/ui/button";
import FriendAvatar from "@/components/FriendAvatar";
import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { useFriendsStore } from "../store/useFriendsStore";
import { INPUT_CLASS } from "../types/friends.types";
import FriendModal from "./FriendModal";

export default function FriendsTab() {
  const { friends, loading, addFriend } = useFriendsStore();
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const key = normalizeFriendName(search);
  const shown = friends.filter((f) =>
    normalizeFriendName(f.name).includes(key),
  );

  async function handleAdd(e: { preventDefault(): void }) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await addFriend(name);
      setNewName("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to add friend",
      );
    } finally {
      setAdding(false);
    }
  }

  return (
    <>
      <form onSubmit={handleAdd} className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Add a friend"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={100}
          className={`flex-1 min-w-0 ${INPUT_CLASS} py-2 text-base sm:text-sm`}
        />
        <Button
          type="submit"
          disabled={!newName.trim()}
          loading={adding}
          size="sm"
          className="shrink-0"
        >
          {!adding && <Plus className="w-4 h-4 mr-1" />}
          Add
        </Button>
      </form>

      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          aria-label="Search friends"
          placeholder="Search friends"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${INPUT_CLASS} w-full py-2 pl-9 text-base sm:text-sm`}
        />
      </div>

      {loading && friends.length === 0 ? (
        <ListRowsSkeleton rows={4} />
      ) : shown.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {friends.length === 0
            ? "No friends yet — add one above, or from a bill"
            : "No one matches your search"}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-card">
          {shown.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => setOpenId(f.id)}
                className="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-accent active:bg-accent"
              >
                <FriendAvatar
                  name={f.name}
                  avatar={f.avatar}
                  friendId={f.id}
                  size={32}
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {f.name}
                </span>
                {f.contactInfo && (
                  <span className="max-w-[40%] truncate text-xs text-muted-foreground">
                    {f.contactInfo}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {openId && (
        <FriendModal friendId={openId} onClose={() => setOpenId(null)} />
      )}
    </>
  );
}
